import { ChevronDown, ChevronUp } from 'lucide-react';
import React from 'react';

/**
 * NumberInput — brand number field. The OS-default spinner is hidden globally
 * (see index.css); this draws brand-coloured up/down chevrons that read
 * correctly in both light and dark mode.
 *
 * Drop-in for `<input type="number">`: pass the same `className` you'd give the
 * input and the chevrons sit inside it on the right. `onChange` receives a
 * Number, or `null` when the field is cleared — matching the schema-driven
 * number-field contract used across the agent editor.
 */
export default function NumberInput({
  value,
  onChange,
  placeholder,
  min,
  max,
  step = 1,
  disabled = false,
  className = '',
  ...rest
}) {
  const clamp = (n) => {
    if (min != null && n < min) return min;
    if (max != null && n > max) return max;
    return n;
  };
  const bump = (dir) => {
    if (disabled) return;
    const cur = value === '' || value == null || Number.isNaN(Number(value)) ? 0 : Number(value);
    // Round to kill float drift (0.1 + 0.2 …) while keeping integer steps exact.
    onChange(clamp(Math.round((cur + dir * step) * 1e8) / 1e8));
  };
  return (
    <div className="relative min-w-0">
      <input
        type="number"
        value={value ?? ''}
        placeholder={placeholder}
        min={min}
        max={max}
        step={step}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value === '' ? null : Number(e.target.value))}
        className={`${className} pr-6`}
        {...rest}
      />
      <div className="absolute right-1 top-1/2 flex -translate-y-1/2 flex-col">
        <button
          type="button"
          tabIndex={-1}
          aria-label="Increase"
          disabled={disabled}
          onClick={() => bump(1)}
          className="flex h-[11px] w-4 items-center justify-center text-[color:var(--trodo-primary)] transition-opacity hover:opacity-70 disabled:opacity-40"
        >
          <ChevronUp size={12} strokeWidth={2.5} />
        </button>
        <button
          type="button"
          tabIndex={-1}
          aria-label="Decrease"
          disabled={disabled}
          onClick={() => bump(-1)}
          className="flex h-[11px] w-4 items-center justify-center text-[color:var(--trodo-primary)] transition-opacity hover:opacity-70 disabled:opacity-40"
        >
          <ChevronDown size={12} strokeWidth={2.5} />
        </button>
      </div>
    </div>
  );
}
